import { createSelector } from '@reduxjs/toolkit'

import { RootState } from '../../../shared/config/store'

export const getFilesPublicLinkPasswordModalStatusSelector = (state) =>
  state.publicLinkFiles.passwordModalOpened

export const getFilesPublicLinkNeedAuthInPublicLinkSelector = (state) =>
  state.publicLinkFiles.needAuthInPublicLink

export const getFilesPublicLinkErrorSelector = (state) =>
  state.publicLinkFiles.error

export const getIsSizeQuotaExceedErrorSelector = (state) =>
  state.publicLinkFiles.isSizeQuotaExceedError

export const getFilesPublicLinkLoadingStatusSelector = (state) =>
  state.publicLinkFiles.status

export const getFilesPublicLinkTotalItemsCountSelector = (state) =>
  state.publicLinkFiles.totalItemsCount

export const getFilesPublicLinkNodeDataSelector = (state) =>
  state.publicLinkFiles.nodeData

export const getFilesPublicLinkPermissionSelector = (state) =>
  state.publicLinkFiles.permission

export const getFilesPublicLinksTokenSelector = (state) =>
  state.publicLinkFiles.token

export const getFilesPublicLinksNotMyResourceHrefSelector = (state) =>
  state.publicLinkFiles.notMyResourseHref

export const getFilesPublicLinkNodesHrefSelector = (state) =>
  state.publicLinkFiles.itemsHref

export const getFilesPublicLinkSelectType = (state) =>
  state.publicLinkFiles.selectType

export const getFilesPublicLinkSelectedItemsCountSelector = (state) =>
  state.publicLinkFiles.selectedItemsIds.length

export const getFilesPublicLinkShareIdSelector = (state) =>
  state.publicLinkFiles.sharedId

export const getFilesPublicLinkBreadcrumbsSelector = (state) =>
  state.publicLinkFiles.breadcrumbs

export const getFilesPublicLinkRootNodeTypeSelector = (state) =>
  state.publicLinkFiles.rootNodeType

export const getFilesPublicLinkSystemFieldsSelector = (state) => state.publicLinkFiles.systemFields

export const getFilesPublicLinkPermissionsGroupsSelector = (state) =>
  state.publicLinkFiles.nodeData?._embedded?.share?.permissions_groups

export const getSystemFieldsValues = (state: RootState) => state.publicLinkFiles.systemFieldsValues

export const getPath = (state: RootState) => state.publicLinkFiles.path

export const getCurrentBoardId = createSelector(
  getPath,
  (paths: string[]) => {
    return paths && paths.length > 1 ? paths[paths.length - 1] : ''
  }
)

export const getNodeFieldsByIdSelector = (state: RootState) => state.publicLinkFiles.nodeFieldsById

export const getActivePublicBoardNodeId = createSelector(
  getFilesPublicLinkNodeDataSelector,
  getCurrentBoardId,
  (nodeData, currentBoardId) => {
    if (currentBoardId) return currentBoardId
    return nodeData?.node_id || nodeData?.id || ''
  }
)

export const getFilesPublicLinkNodeFieldsSelector = createSelector(
  getNodeFieldsByIdSelector,
  getActivePublicBoardNodeId,
  (nodeFieldsById, nodeId) => {
    if (!nodeId) return null
    return nodeFieldsById[nodeId] || null
  }
)

export const getSystemStatuses = createSelector(
  getSystemFieldsValues,
  (data) => {
    if(!data.statusOptions || !data.statusOptions.length) return []
    return data.statusOptions
  }
)

export const getSystemTags = createSelector(
  getSystemFieldsValues,
  (data) => {
    if(!data.tags) return []
    return data.tags
  }
)

const normalizeDescriptionValue = (raw: unknown): string | null => {
  if (typeof raw === 'string') {
    return raw
  }

  if (raw && typeof raw === 'object') {
    const valueProp = (raw as any).value

    if (typeof valueProp === 'string') {
      return valueProp
    }

    if (valueProp && typeof valueProp === 'object') {
      if (typeof valueProp.text === 'string') {
        return valueProp.text
      }

      if (typeof valueProp.html === 'string') {
        return valueProp.html
      }
    }

    if (typeof (raw as any).text === 'string') {
      return (raw as any).text
    }
  }

  return null
}

const getFieldByTitle = (title: string, data) => {
  if(!data) return null
  const statusField = data.find(({ field }) => {
    return field?.title === title
  })
  if(!statusField) return null
  return statusField.field
}

export const getSystemTagsField = createSelector(
  getFilesPublicLinkSystemFieldsSelector,
  (data) => {
    return getFieldByTitle('tags', data)
  }
)

export const getDescriptionField = createSelector(
  getFilesPublicLinkSystemFieldsSelector,
  (data) => {
    return getFieldByTitle('description', data)
  }
)

export const getDescriptionFieldId = createSelector(
  getDescriptionField,
  (field) => {
    return field?.id ?? null
  }
)

export const getPublicBoardDescription = createSelector(
  getFilesPublicLinkNodeFieldsSelector,
  getFilesPublicLinkNodeDataSelector,
  getActivePublicBoardNodeId,
  (nodeFields, nodeData, activeNodeId) => {
    const sanitize = (value: string | null | undefined): string => {
      if (typeof value !== 'string') {
        return ''
      }

      return value.trim().length > 0 ? value : ''
    }

    const descriptionFromFields = sanitize(normalizeDescriptionValue(nodeFields?.description?.value))
    if (descriptionFromFields) {
      return descriptionFromFields
    }

    const rootNodeId = nodeData?.node_id || nodeData?.id || ''
    const isRootNode = activeNodeId === rootNodeId

    if (isRootNode) {
      const descriptionFromNode = sanitize(normalizeDescriptionValue(nodeData?.description))
      if (descriptionFromNode) {
        return descriptionFromNode
      }
    }

    return ''
  }
)

export const getFilesPublicLinkNodeHrefSelector = (state: RootState) =>
  state.publicLinkFiles.nodeHref
